import { useState, useEffect, useCallback } from 'react';
import { GAME_CONFIG } from '../config/gameConfig';
import { RHYTHM_CONFIG } from '../config/RhythmConfig';

export const useRhythmInput = ({
  showResult,
  currentTimeRef,
  currentBeatmap,
  audioController,
  showJudge,
  baseScore,
  evaluateGroup,
  activeTouchesRef,
  groupTouchedLanesRef,
  groupTimeoutsRef,
  laneQueuesRef,
  groupedNotesRef,
  missedNotesRef,
  comboRef,
  setCombo,
  MISS_WINDOW,
  GOOD_WINDOW,
  rhythmConfig = RHYTHM_CONFIG
}) => {
  const [activeLanes, setActiveLanes] = useState(new Set());

  // 레인 클릭 판정
  const handleLaneClick = useCallback((laneIndex) => {
    if (showResult) return;

    // 터치·클릭 피드백
    setActiveLanes(prev => {
      const s = new Set(prev);
      s.add(laneIndex);
      return s;
    });
    setTimeout(() => setActiveLanes(prev => {
      const s = new Set(prev);
      s.delete(laneIndex);
      return s;
    }), rhythmConfig.LANE_CLICK_DURATION);

    // 정확한 오디오 시간 사용 (보간 없이)
    const gameTime = currentTimeRef.current() * 1000;

    // 이 레인의 노트 큐에서 맨 앞 시각만 확인
    const queue = laneQueuesRef.current[laneIndex];
    if (!queue?.length) return;
    const noteTime = queue[0];

    // MISS_WINDOW 이내에 있지 않으면 무시
    if (Math.abs(gameTime - noteTime) > MISS_WINDOW) return;

    // 이제 그룹 키와 note 객체를 가져옴
    const key = noteTime;
    const group = groupedNotesRef.current[key];
    if (!group) return;

    // 터치 기록
    if (!groupTouchedLanesRef.current[key]) {
      groupTouchedLanesRef.current[key] = new Set();
    }
    groupTouchedLanesRef.current[key].add(laneIndex);

    // 조건 체크: 노트 1개인데 2개 이상 레인을 누르면 즉시 Miss
    if (group.length === 1) {
      const allActiveTouches = new Set();
      activeTouchesRef.current.forEach((laneIndex, eventKey) => {
        if (typeof laneIndex === 'number') {
          allActiveTouches.add(laneIndex);
        }
      });

      if (allActiveTouches.size >= 2) {
        group.forEach(note => {
          const id = `${note.time}-${note.lane}`;
          missedNotesRef.current.add(id);
        });
        comboRef.current = 0;
        setCombo(0);
        showJudge('Miss', group[0]);

        if (groupTimeoutsRef.current[key]) {
          clearTimeout(groupTimeoutsRef.current[key]);
          delete groupTimeoutsRef.current[key];
        }
        delete groupTouchedLanesRef.current[key];

        group.forEach(note => {
          const q = laneQueuesRef.current[note.lane];
          if (q?.[0] === note.time) q.shift();
        });
        return;
      }
    }

    // 스트럼 스타일 Miss 타임아웃 (GOOD_WINDOW 후)
    if (group.length > 1 && !groupTimeoutsRef.current[key]) {
      const delay = Math.max((key + GOOD_WINDOW) - gameTime, 0);
      groupTimeoutsRef.current[key] = setTimeout(() => {
        evaluateGroup(key, group);
        delete groupTimeoutsRef.current[key];
      }, delay);
    }

    // 모든 레인 입력 시 즉시 평가 (지연 추가)
    if (groupTouchedLanesRef.current[key].size === group.length) {
      if (groupTimeoutsRef.current[key]) {
        clearTimeout(groupTimeoutsRef.current[key]);
        delete groupTimeoutsRef.current[key];
      }
      setTimeout(() => evaluateGroup(key, group), 5);
    }
  }, [showResult, currentBeatmap, audioController, showJudge, baseScore, evaluateGroup, MISS_WINDOW, GOOD_WINDOW]);

  // 키보드 입력
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        try {
          audioController.unlock();
          if (!audioController.isPlaying) {
            audioController.play();
          }
        } catch (error) {
          //console.warn('키보드 입력 시 오디오 resume 실패:', error);
        }
      }

      const laneIndex = rhythmConfig.KEY_MAP[e.key.toLowerCase()];
      if (laneIndex !== undefined) {
        const uniqueKey = `keyboard_${e.key.toLowerCase()}`;
        activeTouchesRef.current.set(uniqueKey, laneIndex);

        setTimeout(() => {
          handleLaneClick(laneIndex);
        }, 5);
      }
    };

    const handleKeyUp = (e) => {
      const laneIndex = rhythmConfig.KEY_MAP[e.key.toLowerCase()];
      if (laneIndex !== undefined) {
        const uniqueKey = `keyboard_${e.key.toLowerCase()}`;
        activeTouchesRef.current.delete(uniqueKey);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [showResult, handleLaneClick]);

  // 멀티터치 지원 핸들러
  const handleLanePress = (laneIndex) => (e) => {
    e.preventDefault();

    if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      try {
        audioController.unlock();
        if (!audioController.isPlaying) {
          audioController.play();
        }
      } catch (error) {
        //console.warn('레인 터치 시 오디오 resume 실패:', error);
      }
    }

    if (e.pointerId !== undefined && e.pointerId !== null) {
      activeTouchesRef.current.set(e.pointerId, laneIndex);
    } else {
      const uniqueKey = `touch_${Date.now()}_${laneIndex}_${Math.random()}`;
      activeTouchesRef.current.set(uniqueKey, laneIndex);
    }

    setActiveLanes(prev => {
      const newSet = new Set(prev);
      newSet.add(laneIndex);
      return newSet;
    });

    handleLaneClick(laneIndex);
  };

  const handleLaneRelease = (laneIndex) => (e) => {
    e.preventDefault();

    if (e.pointerId !== undefined) {
      activeTouchesRef.current.delete(e.pointerId);
    }

    setActiveLanes(prev => {
      const newSet = new Set(prev);
      newSet.delete(laneIndex);
      return newSet;
    });
  };

  const handleLaneCancel = (laneIndex) => (e) => {
    e.preventDefault();

    if (e.pointerId !== undefined) {
      activeTouchesRef.current.delete(e.pointerId);
    }

    setActiveLanes(prev => {
      const newSet = new Set(prev);
      newSet.delete(laneIndex);
      return newSet;
    });
  };

  return {
    activeLanes,
    setActiveLanes,
    handleLaneClick,
    handleLanePress,
    handleLaneRelease,
    handleLaneCancel
  };
};
